import * as React from "react";
import { cn } from "@/lib/utils";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";
import { GradientText, SectionLabel } from "./GradientText";
import { Quote } from "lucide-react";

interface Testimonial {
  id: string;
  quote: string;
  author: string;
  role: string;
  avatar?: string;
}

interface TestimonialsProps {
  title: string;
  subtitle?: string;
  testimonials: Testimonial[];
}

export function Testimonials({
  title,
  subtitle,
  testimonials,
}: TestimonialsProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="testimonials" className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "text-center max-w-3xl mx-auto mb-16 lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel className="mb-6">Testimonials</SectionLabel>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4">
            <GradientText>{title}</GradientText>
          </h2>

          {subtitle && (
            <p className="text-muted-foreground text-base lg:text-lg">{subtitle}</p>
          )}
        </div>

        {/* Testimonials grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <SpotlightCard
              key={testimonial.id}
              className={cn(
                "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              )}
              style={{
                transitionDelay: isVisible ? `${index * 80}ms` : "0ms",
              }}
            >
              <SpotlightCardContent className="h-full flex flex-col p-6">
                {/* Quote icon */}
                <Quote className="w-8 h-8 text-accent/40 mb-4" />

                {/* Quote text */}
                <blockquote className="text-foreground text-base leading-relaxed mb-6 flex-1">
                  "{testimonial.quote}"
                </blockquote>

                {/* Author */}
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full overflow-hidden",
                      "bg-accent/10",
                      "flex items-center justify-center"
                    )}
                  >
                    {testimonial.avatar ? (
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.author}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                          target.parentElement!.innerHTML = `<span class="text-accent font-semibold text-sm">${testimonial.author
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}</span>`;
                        }}
                      />
                    ) : (
                      <span className="text-accent font-semibold text-sm">
                        {testimonial.author
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </span>
                    )}
                  </div>

                  {/* Name & Role */}
                  <div>
                    <div className="font-medium text-foreground text-sm">
                      {testimonial.author}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </SpotlightCardContent>
            </SpotlightCard>
          ))}
        </div>
      </div>
    </section>
  );
}
